export type ChangeCategory = 'product' | 'api' | 'security';
export type ChangeType = 'new' | 'improved' | 'fixed' | 'breaking' | 'deprecated';

export type ChangelogEntry = {
  id: string;
  date: string;
  category: ChangeCategory;
  type: ChangeType;
  title: string;
  description: string;
  items?: string[];
  link?: { label: string; href: string };
};

export const changelogEntries: ChangelogEntry[] = [
  {
    id: 'kora-ai',
    date: '2026-10-27',
    category: 'product',
    type: 'new',
    title: 'Kora AI: reply drafting and a resolution agent',
    description:
      'Kora AI answers from your own material: exceptions you write, files and entries in your knowledge base, and your published help articles, in that order of priority. Every answer shows its sources.',
    items: [
      'Reply drafting: a Draft reply button in the ticket composer for the roles you choose. Your team edits and sends. 1 credit per draft; no confident draft means no charge',
      'Resolution agent: answers customer-portal and dashboard tickets, in Auto or Approval required mode, and hands billing, legal, security and unhappy customers to a person. 3 credits per confirmed resolution',
      'Knowledge: add exceptions, upload files or write entries, and reorder which source wins',
      'Activity: every draft and answer with its sources, credits used, and approve, edit or reject',
    ],
    link: { label: 'Open Kora AI', href: '/dashboard/kora-ai' },
  },
  {
    id: 'portal-ai-assistant',
    date: '2026-10-26',
    category: 'product',
    type: 'new',
    title: 'A new customer portal with an AI request assistant',
    description:
      'Your customers now describe a problem once, in their own words. The assistant answers from your help articles when it can, and otherwise prepares a clear request for your team. The conversation carries on in the same chat after it is submitted.',
    items: [
      'Chat-style new request that suggests relevant help articles before anything is submitted',
      'Review the structured request, then submit or add more details',
      'Requests open as a chat with replies, attachments and live updates',
      'Redesigned portal home, sign-in and request list on your branding',
      'Branding can now find your logo on your website if you have not set one',
    ],
  },
  {
    id: 'customer-portal-workspace-page',
    date: '2026-10-25',
    category: 'product',
    type: 'improved',
    title: 'Customer Portal gets its own page',
    description:
      'Run your customer portal from one place in the sidebar, right below Helpdesk. Setup, customers, the tickets they raised and data requests are now four tabs, and enabling the portal works again.',
    items: [
      'New Tickets tab: every request raised through the portal, opening in the usual ticket panel',
      'Create a ticket on behalf of a customer who has signed in',
      'Fixed: turning the portal on no longer shows "Workspace not found"',
    ],
  },
  {
    id: 'branding-match-website',
    date: '2026-10-25',
    category: 'product',
    type: 'new',
    title: 'Match your website in one click',
    description:
      'Branding can now read your website and propose a logo, colors and fonts. You review the draft and save it. Until you set your own, your helpdesk, portal and customer emails use Kora\'s warm default look.',
    items: [
      'Free, up to five tries a day',
      'Nothing is saved until you press Save',
      'New default palette for helpdesks, portals and emails that have not set colors',
    ],
  },
  {
    id: 'ticket-panel-redesign',
    date: '2026-10-25',
    category: 'product',
    type: 'improved',
    title: 'A calmer ticket panel',
    description:
      'Opening a ticket now shows everything from the top: the title, who it is from, who owns it and the SLA clocks, then the description and one conversation. Your tickets, permissions and replies work exactly as before.',
    items: [
      'One clear next action (Claim, Resolve or Reopen); Close and Merge sit in a More menu',
      'Change status, priority and assignee right from the ticket header',
      'Replies, internal notes and activity in a single timeline',
      'A pinned reply box that turns amber for internal notes',
      'Keyboard shortcuts: E to resolve, R to reply, N for a note',
    ],
  },
  {
    id: 'dashboard-redesign',
    date: '2026-10-19',
    category: 'product',
    type: 'improved',
    title: 'A new look for the Kora dashboard',
    description:
      'The whole dashboard now shares the warm Kora look: clearer pages, a new sidebar and a light, dark or system theme. Your data, permissions and credits work exactly as before.',
    items: [
      'Redesigned Dashboard, Get Started, Tickets, Helpdesk, AI Analyst, Files, Snippets, Team and Settings',
      'Light, dark and system themes with a switch in the sidebar',
      'Ticket status always shows a dot and a word, never colour alone',
      'Works on phones: tables, menus and settings adapt to narrow screens',
    ],
  },
  {
    id: 'customer-portal',
    date: '2026-10-18',
    category: 'product',
    type: 'new',
    title: 'Customer portal — your customers can finally sign in',
    description:
      'Every workspace can switch on a branded portal at /p/portal/<your-link>. Customers sign in with an emailed link (no password, no account), follow their own requests, reply, raise new ones and download or delete their data. A new request costs 1 credit like any ticket; everything else is free.',
    items: [
      'Passwordless magic-link sign-in; open to anyone with an email, or invited customers only',
      'Customers see only the requests they raised — never other customers, assignees, SLA or internal notes',
      'Reply to agents, reopen resolved requests, start a follow-up on closed ones, attach files',
      'Agents choose Reply to customer or Internal note; internal notes are never shown or emailed to the customer',
      'One workspace Branding page (Settings → Branding) now drives the helpdesk, portal and customer emails',
      'GDPR built in: customers can download and delete their own data; owners can export or erase on request, with an audit log',
      'Safety controls: hourly and per-customer caps, block customers, kill switch, automatic retention clean-up',
    ],
  },
  {
    id: 'help-center',
    date: '2026-10-05',
    category: 'product',
    type: 'new',
    title: 'Helpdesk — a branded knowledge base blog',
    description:
      'Every workspace now has a public, branded helpdesk: write articles in a Notion-style editor, publish to your own link, and watch visitors in built-in analytics. 1 credit per published article — edits, branding, search and subscriptions are free.',
    items: [
      'Public helpdesk at /p/helpdesk/<your-link> with article listing, full-text search and helpful votes',
      'Claim up to 5 custom links (a-z, 0-9) — every old link keeps working via redirects',
      'Notion-style editor: "/" block menu, markdown shortcuts, floating toolbar, tables, images and videos (YouTube/Vimeo/Loom embeds or MP4/WebM uploads)',
      'Per-article controls: slug, summary, cover image, meta title/description, public or unlisted, and a per-article search-engine (sitemap) switch',
      'Branding: accent color, separate light/dark text + background colors, title and body fonts — monochrome by default with a light/dark/system toggle',
      'Built-in analytics: visitors, pageviews, referrers, top articles, and on-site searches (zero-results become your content backlog)',
      'Email subscriptions with bot protection; export to CSV anytime',
      'Revision history (last 50) with restore, plus 7-day draft share links you can revoke',
      'Help widget mode: embed your whole helpdesk with one script tag',
      'New webhook events article.published / article.updated, and public API endpoints GET /public/v1/articles + /public/v1/articles/:slug',
    ],
    link: { label: 'Helpdesk guide', href: '/knowledge-base-software' },
  },
  {
    id: 'automation-rules',
    date: '2026-10-03',
    category: 'product',
    type: 'new',
    title: 'Automation rules',
    description:
      'If-this-then-that rules for tickets: when a trigger fires and conditions match, execute actions automatically. Free on every workspace.',
    items: [
      '12 triggers: ticket created, status/priority/assignee changed, comment added, SLA breached, no-response-for-N-hours, waiting-for-N-hours, and more',
      '9 condition operators: equals, contains, is-one-of, email domain match, ticket source, and more',
      '7 actions: set priority, set status, assign, add comment, fire webhook, send email to members, re-run AI structuring',
      'Dry-run testing — see which tickets would match before enabling a rule',
      'Rule ordering with stop-on-match; per-rule run history log',
      'Rate caps per workspace (500/hr) and per rule (100/hr) with clear skip logging',
      'Time-based triggers fire once per ticket and re-arm only when the condition resets',
      'Safety: kill switch, per-workspace toggle, automation comments never satisfy SLA first-response, reporter emails blocked',
    ],
    link: { label: 'Settings → Automation', href: '/dashboard/settings' },
  },
  {
    id: 'managed-categories',
    date: '2026-10-03',
    category: 'product',
    type: 'new',
    title: 'Managed categories',
    description:
      'Ticket categories are now a governed list per workspace — pick them at signup, manage them in Settings, and let AI create new ones when nothing fits (your choice).',
    items: [
      '"Other" is a permanent system category on every workspace',
      'Category picker during workspace creation + the "Let AI create categories" toggle',
      'AI structuring picks from your list; unmatched tickets get a new AI-created category (when enabled) or Other',
      'Settings → Categories: rename (updates all tickets), delete (only when unused)',
      'Unknown categories in ticket creation and the public API are auto-created — integrations never break',
      'New public endpoint: GET /public/v1/categories',
    ],
    link: { label: 'Categories API', href: '/docs' },
  },
  {
    id: 'sla-tracking',
    date: '2026-10-01',
    category: 'product',
    type: 'new',
    title: 'SLA tracking',
    description:
      'Set response and resolution deadlines per priority, run the clocks on your business-hours calendar, and know the moment a promise slips — free on every workspace.',
    items: [
      'Per-priority first-response & resolution targets, with optional per-category policies',
      'Business-hours calendars: timezone, working days, hours, and holidays',
      'Clocks automatically pause while a ticket waits on the customer or another team',
      'Live countdown badges on every ticket; breached filter and SLA-urgent sort',
      'Breach alerts to assignees & owners (once per breach) + ticket.sla_breach webhook',
      'First-response and resolution compliance on the dashboard',
    ],
    link: { label: 'SLA tracking guide', href: '/sla-tracking-software' },
  },
  {
    id: 'payg-credits',
    date: '2026-09-27',
    category: 'product',
    type: 'new',
    title: 'Pay-as-you-go pricing',
    description:
      'Kora now runs on credits — pay per ticket, not per seat. $1 = 10 credits, 1 credit per ticket created, 1 credit per AI Analyst answer. Everything collaborative (members, comments, API, structuring) stays free.',
    items: [
      'Claim 1,000 bonus credits (30 days) + 100 base credits (10 years) on signup — no card required',
      'Credit packs from $5 ($5/$10/$20/$50/$100 or custom); first recharge gets +30% bonus credits',
      'New Billing tab in Settings: balance, usage breakdown, and full activity ledger',
      'Public API usage metered at 1 credit per 400 responses; AI Analyst answers 1 credit each, charged only on success',
      'Wallet can overdraw to −10 credits so inbound work is never lost; existing workspaces already credited',
      'Payments handled by Dodo Payments (cards, UPI & more; tax included in checkout)',
    ],
    link: { label: 'See pricing', href: '/pricing' },
  },
  {
    id: 'status-page-monitoring',
    date: '2026-08-24',
    category: 'product',
    type: 'new',
    title: 'Status Page',
    description:
      'A public status page where you can check if Kora is up, see how fast it\u2019s responding, and view any past or ongoing incidents \u2014 all in real time.',
    items: [
      'See current uptime status at a glance \u2014 operational, degraded, or down',
      'View response time trends over the last 24 hours, 7 days, 30 days, or 90 days',
      '90-day uptime history with daily breakdown \u2014 spot patterns at a glance',
      'Automatic incident tracking with start time, resolution time, and duration',
      'Page auto-refreshes every 60 seconds so you always see the latest state',
    ],
    link: { label: 'View status', href: '/status' },
  },
  {
    id: 'launch-security-hardening',
    date: '2026-08-23',
    category: 'security',
    type: 'fixed',
    title: 'Security & Reliability Improvements',
    description:
      'A series of security and reliability upgrades to keep your data safer and your integrations more dependable as we prepare for public launch.',
    items: [
      'Webhook secrets are now encrypted at rest \u2014 your integration credentials are protected even in the unlikely event of a data breach',
      'File attachments now use expiring links instead of permanent URLs \u2014 shared links automatically expire after 1 hour',
      'Failed webhook deliveries now retry automatically with smart backoff \u2014 your integrations recover from temporary outages without manual intervention',
      'Embedded sessions can now be revoked on demand \u2014 instantly cut off access if a session is compromised or no longer needed',
      'Rate limiting is now faster and more accurate \u2014 your API requests won\u2019t be incorrectly throttled during traffic spikes',
      'Automatic cleanup of old data keeps the API fast and responsive as your workspace grows',
      'Invalid and expired session tokens now return the same error \u2014 attackers can\u2019t tell the difference to enumerate active sessions',
      'Webhook deliveries now time out after 10 seconds \u2014 no more hanging connections when a target server is slow',
    ],
  },
  {
    id: 'ticketing-infra-as-a-service',
    date: '2026-08-22',
    category: 'api',
    type: 'new',
    title: 'Build Your Own Helpdesk with the Public API',
    description:
      '14 new API endpoints that let you build a fully custom support experience on top of Kora\u2019s backend \u2014 your own UI, your own workflows, no dashboard required.',
    items: [
      'Claim tickets programmatically \u2014 build custom assignment flows for your team',
      'Merge duplicate tickets via API \u2014 comments, reactions, and attachments all move to the parent ticket',
      'Bulk update up to 100 tickets at once \u2014 change status or assignee in a single request',
      'Upload and list file attachments through the API \u2014 no dashboard needed',
      'Full webhook management \u2014 create, update, delete, test, and view delivery history programmatically',
      'Invite team members and update roles via API \u2014 automate onboarding',
      'Poll for new comments incrementally \u2014 build realtime chat-like experiences on top of tickets',
    ],
    link: { label: 'API docs', href: '/docs' },
  },
  {
    id: 'api-docs-categorization',
    date: '2026-08-22',
    category: 'product',
    type: 'improved',
    title: 'Faster API Documentation Navigation',
    description:
      'API docs are now organized by resource category so you can find the endpoint you need in seconds instead of scrolling through a flat list.',
    items: [
      'Endpoints grouped by what you\u2019re working with \u2014 Tickets, Comments, Attachments, Members, Webhooks, and more',
      'Code examples in cURL, JavaScript, Python, and Go for every endpoint',
      'Quick Start section to make your first API request in under a minute',
      'Right-side table of contents that tracks your position on the page',
    ],
    link: { label: 'View docs', href: '/docs' },
  },
  {
    id: 'db-schema-cleanup',
    date: '2026-08-22',
    category: 'api',
    type: 'improved',
    title: 'Faster API Performance',
    description:
      'Behind-the-scenes cleanup that makes the API faster and more reliable \u2014 no action needed on your end.',
    items: [
      'Webhook creation via the API is now more reliable',
      'Rate limit headers now accurately reflect your remaining quota',
      'Dashboard metrics now validate month inputs to prevent errors',
      'Tighter input validation across the board \u2014 bad requests fail faster with clearer errors',
    ],
  },
  {
    id: 'ticket-merge-bulk',
    date: '2026-07-13',
    category: 'product',
    type: 'new',
    title: 'Merge Duplicate Tickets & Bulk Actions',
    description:
      'Clean up duplicate tickets in one click and update multiple tickets at once instead of editing them one by one.',
    items: [
      'Merge duplicate tickets \u2014 all comments, reactions, and attachments move to the parent ticket automatically',
      'The merged ticket is closed and linked to the parent, so nothing gets lost',
      'Select multiple tickets and change their status or assignee in one action',
      'Every merge and bulk update is logged in the activity feed for full auditability',
    ],
  },
  {
    id: 'realtime-comments',
    date: '2026-07-13',
    category: 'product',
    type: 'new',
    title: 'Realtime Comments',
    description:
      'Comments on tickets now update automatically \u2014 no more refreshing the page to see if someone replied.',
    items: [
      'New comments appear within 5 seconds without a page refresh',
      'Works across multiple agents \u2014 everyone sees the same conversation in real time',
      'No duplicates \u2014 the system tracks what you\u2019ve already seen',
    ],
  },
  {
    id: 'public-api-members-webhooks',
    date: '2026-07-13',
    category: 'api',
    type: 'new',
    title: 'Manage Members & Webhooks via API',
    description:
      'You can now list workspace members and create webhooks programmatically \u2014 automate your team setup and integrations.',
    items: [
      'List all active workspace members via the API',
      'Create webhooks with event subscriptions and secret tokens \u2014 no dashboard needed',
      'Full authentication and rate limiting built in',
    ],
    link: { label: 'API docs', href: '/docs#list-members' },
  },
  {
    id: 'api-docs-redesign',
    date: '2026-07-08',
    category: 'product',
    type: 'improved',
    title: 'Interactive API Docs',
    description:
      'A completely redesigned documentation experience that lets you try API requests right from the browser.',
    items: [
      'Try-It explorer \u2014 send real API requests without leaving the docs',
      'Code examples in cURL, JavaScript, and Python with syntax highlighting',
      'Cmd+K command palette to jump to any endpoint instantly',
      'Copy-paste embed examples for React, Vue, Next.js, and plain HTML',
    ],
    link: { label: 'View docs', href: '/docs' },
  },
  {
    id: 'webhooks-launch',
    date: '2026-07-08',
    category: 'api',
    type: 'new',
    title: 'Webhooks \u2014 Real-time Notifications',
    description:
      'Get notified the moment something happens with your tickets \u2014 no polling required. Subscribe to the events you care about and Kora will push updates to your server.',
    items: [
      '7 event types: ticket created, updated, resolved, closed, reopened, assigned, and claimed',
      'Up to 10 webhooks per workspace \u2014 route different events to different endpoints',
      'HMAC-SHA256 signature verification \u2014 confirm every notification is genuinely from Kora',
      'Automatic retries with exponential backoff \u2014 your integration recovers from temporary outages',
      'Delivery logs visible in the dashboard \u2014 see exactly when and if each notification was delivered',
    ],
    link: { label: 'Webhook docs', href: '/docs#webhooks-overview' },
  },
  {
    id: 'embedded-sessions',
    date: '2026-07-01',
    category: 'product',
    type: 'new',
    title: 'Embed Kora Inside Your Product',
    description:
      'Drop the Kora ticket board directly into your app via iframe \u2014 your users get full ticket management without ever leaving your product or logging in separately.',
    items: [
      'One-time session URLs with configurable duration \u2014 from 5 minutes to 24 hours',
      'Users are auto-authenticated with the right permissions \u2014 no separate login',
      'Light and dark theme support to match your product\u2019s look',
      'Copy-paste embed examples for HTML, React, Vue, Next.js, and Python',
    ],
    link: { label: 'Embedded sessions docs', href: '/docs#embedded-overview' },
  },
  {
    id: 'custom-api-creation',
    date: '2026-06-20',
    category: 'api',
    type: 'new',
    title: 'Custom API Endpoints',
    description:
      'Growth and Custom plan customers can now request bespoke API endpoints tailored to their specific workflows.',
    items: [
      'Available on Growth and Custom plans',
      'Tell us what you need \u2014 we build endpoints that fit your workflow',
      'Full integration with your existing workspace permissions and roles',
    ],
  },
  {
    id: 'team-analytics',
    date: '2026-06-15',
    category: 'product',
    type: 'improved',
    title: 'Richer Team Analytics',
    description:
      'See how your team is performing with enhanced charts, trend data, and metrics you can act on.',
    items: [
      'Daily ticket creation trends \u2014 spot spikes before they become problems',
      'Status distribution and category breakdowns \u2014 understand where your volume is coming from',
      'Per-user ticket counts \u2014 see who\u2019s handling the most work',
      'Pull monthly metrics via the API \u2014 build your own dashboards',
    ],
  },
  {
    id: 'csv-export',
    date: '2026-06-10',
    category: 'product',
    type: 'new',
    title: 'Export Tickets to CSV',
    description:
      'Export your tickets to a spreadsheet for reporting, archiving, or importing into other tools.',
    items: [
      'Available on all plans, no upgrade required',
      'Filter by date range, status, category, and assignee \u2014 export only what you need',
      'Includes all ticket fields and metadata',
    ],
  },
  {
    id: 'plan-limits-update',
    date: '2026-06-01',
    category: 'product',
    type: 'improved',
    title: 'Higher API Limits & Plan Updates',
    description:
      'We\u2019ve increased API rate limits and expanded feature availability so growing teams can do more. (Historical entry: plans were retired in September 2026; API rate limits now follow wallet balance, see the docs.)',
    items: [
      'Free plan: 5,000 API requests per day',
      'Growth plan: 50,000 API requests per day',
      'Custom plan: tailored API limits and custom endpoint creation',
      'Embedded sessions now available on all plans \u2014 no more paywall for embedding',
    ],
    link: { label: 'View pricing', href: '/pricing' },
  },
  {
    id: 'ai-analyst',
    date: '2026-05-20',
    category: 'product',
    type: 'new',
    title: 'AI Analyst',
    description:
      'Ask questions about your ticket data in plain English and get instant, accurate answers \u2014 text-to-SQL over your own workspace, no dashboards to build.',
    items: [
      'Natural-language Q&A over tickets, comments, and team activity',
      'Workspace-isolated queries \u2014 every answer is scoped to your data only',
      'Conversation history so you can iterate on a question',
      'Available for owner, admin, and manager roles (1 credit per answer)',
    ],
  },
  {
    id: 'role-based-access',
    date: '2026-05-15',
    category: 'product',
    type: 'improved',
    title: 'Role-Based Access Control',
    description:
      'Granular permissions so the right people have the right access \u2014 and nothing more.',
    items: [
      'Owner: full control including billing and workspace deletion',
      'Admin: manage members, snippets, and all tickets',
      'Manager: manage tickets, snippets, and view analytics',
      'Operations: create and manage tickets',
    ],
  },
  {
    id: 'kora-launch',
    date: '2026-05-01',
    category: 'product',
    type: 'new',
    title: 'Kora is Live',
    description:
      'Kora is here \u2014 the collaborative issue tracker built for modern ops teams.',
    items: [
      'Ticket management with custom categories and priorities',
      'Real-time collaboration with comments and attachments',
      'Workspace-scoped REST API for building your own integrations',
      'Team management with role-based permissions',
    ],
  },
];
